import { requestJson } from "@/lib/api";
import type { Account, AccountKind, CompanyProfile, ClientType, CommercialStatus } from "@/lib/types";

export type AuthRole = "CLIENT" | "ADMIN";

export interface AuthAccountResponse {
  id: string;
  email: string;
  role: AuthRole;
  status: CommercialStatus;
  profile: CompanyProfile | null;
  /** Nome do funcionário (staff), aditivo — usado na saudação de boas-vindas. */
  name?: string | null;
  priceTableId?: string | null;
  passwordResetRequired?: boolean;
  /** {@code true} quando a conta já tem senha local (define se a troca exige a senha atual). */
  hasPassword?: boolean;
  deliveryUnitId?: string | null;
  accountKind?: AccountKind;
  permissions?: string[];
}

export interface AuthCredentials {
  email: string;
  password: string;
}

export interface CommercialProfilePayload {
  companyName: string;
  tradeName?: string | null;
  cnpj: string;
  phone: string;
  contactName: string;
  addressCep: string;
  addressLogradouro: string;
  addressNumero: string;
  addressComplemento?: string;
  addressBairro: string;
  addressCidade: string;
  addressUf: string;
  clientType: ClientType;
}

export function toAccount(response: AuthAccountResponse): Account {
  return {
    id: response.id,
    email: response.email,
    name: response.name ?? null,
    role: response.role,
    status: response.status,
    profile: response.profile ? { ...response.profile } : null,
    priceTableId: response.priceTableId ?? undefined,
    deliveryUnitId: response.deliveryUnitId ?? null,
    accountKind: response.accountKind,
    permissions: response.permissions ?? [],
    passwordResetRequired: response.passwordResetRequired ?? false,
    hasPassword: response.hasPassword ?? false,
  };
}

export function signupAccount(credentials: AuthCredentials) {
  return requestJson<AuthAccountResponse>("/api/v1/auth/signup", {
    method: "POST",
    body: credentials,
  });
}

export function loginAccount(credentials: AuthCredentials) {
  return requestJson<AuthAccountResponse>("/api/v1/auth/login", {
    method: "POST",
    body: credentials,
  });
}

/**
 * Define/altera a senha da conta AUTENTICADA (exige sessão ativa — nunca o e-mail como prova):
 *  - conta sem senha (ex.: login Google) cria a primeira — passe {@code currentPassword = null}; a sessão
 *    Google é a prova de identidade e o acesso Google é preservado (login-duplo);
 *  - conta que já tem senha precisa informar a senha atual correta.
 */
export function setPassword(currentPassword: string | null, newPassword: string) {
  return requestJson<AuthAccountResponse>("/api/v1/auth/password", {
    method: "POST",
    body: { currentPassword, newPassword },
  });
}

export function fetchCurrentAccount() {
  return requestJson<AuthAccountResponse>("/api/v1/auth/me");
}

export function logoutAccount(): Promise<void> {
  return requestJson<void>("/api/v1/auth/logout", { method: "POST" });
}

export function completeOnboarding(profile: CommercialProfilePayload) {
  return requestJson<AuthAccountResponse>("/api/v1/auth/onboarding", {
    method: "PUT",
    body: profile,
  });
}